import { useSyncExternalStore } from 'react';
import { DEFAULT_EXERCISE_MINUTES, EXERCISE_MINUTE_OPTIONS } from '../domain/exercise';

/**
 * Account › Preferences › Exercise duration: how long each exercise's timer runs. A store
 * every screen can watch; `configureExerciseMinutes` plugs the platform's storage in at the
 * composition root (in memory only until then), and `useExerciseMinutesSync` keeps it on the
 * person's account. Changing it applies to the next exercise, never to one already running.
 */
export interface ExerciseMinutesStorage {
  load(): number | null;
  save(minutes: number): void;
  /** A choice made on this device that is not on the account yet; kept across restarts. */
  loadUnsaved(): number | null;
  saveUnsaved(minutes: number | null): void;
}

let storage: ExerciseMinutesStorage | undefined;
let minutes = DEFAULT_EXERCISE_MINUTES;
/** Whether the device holds a duration of its own rather than the default. */
let held = false;
/** Chosen on this device and not yet on the account. */
let unsaved: number | undefined;
/** The choice Core refused outright, until the person retries or chooses again. */
let refused: number | undefined;
/** Changes each time a session ends. */
let generation = 0;
const listeners = new Set<() => void>();
const choiceListeners = new Set<(minutes: number) => void>();

const isOption = (value: number | null): value is number =>
  value !== null && EXERCISE_MINUTE_OPTIONS.includes(value);

export function configureExerciseMinutes(adapter: ExerciseMinutesStorage): void {
  storage = adapter;
  const stored = adapter.load();
  minutes = isOption(stored) ? stored : DEFAULT_EXERCISE_MINUTES;
  held = isOption(stored);
  const waiting = adapter.loadUnsaved();
  unsaved = isOption(waiting) ? waiting : undefined;
  refused = undefined;
  for (const listener of listeners) listener();
}

function change(next: number): boolean {
  if (!isOption(next) || next === minutes) return false;
  minutes = next;
  held = true;
  storage?.save(next);
  for (const listener of listeners) listener();
  return true;
}

/** The person's choice on Account: kept on the device and sent to their account. */
export function setExerciseMinutes(next: number): void {
  if (!change(next)) return;
  unsaved = next;
  storage?.saveUnsaved(next);
  if (refused !== undefined) {
    refused = undefined;
    for (const listener of listeners) listener();
  }
  for (const listener of choiceListeners) listener(next);
}

/**
 * The duration stored on the person's account: kept on the device, not sent back. Ignored
 * while a choice made here is not on the account yet.
 */
export function adoptExerciseMinutes(next: number): void {
  if (unsaved === undefined) change(next);
}

/** `saved` is on the account now; a later choice still waits for its own save. */
export function exerciseMinutesSaved(saved: number): void {
  if (unsaved !== saved) return;
  unsaved = undefined;
  storage?.saveUnsaved(null);
}

/**
 * Core refused `minutes` outright: the account's value applies again until the person retries
 * or chooses another. Ignored while a later choice is waiting for its own save.
 */
export function exerciseMinutesRefused(minutes: number): void {
  if (unsaved !== undefined && unsaved !== minutes) return;
  unsaved = undefined;
  storage?.saveUnsaved(null);
  refused = minutes;
  for (const listener of listeners) listener();
}

/** The person's session ended: a choice still waiting is not the next person's to save. */
export function forgetUnsavedExerciseMinutes(): void {
  generation += 1;
  unsaved = undefined;
  refused = undefined;
  storage?.saveUnsaved(null);
  for (const listener of listeners) listener();
}

/** Which session a save belongs to: it changes each time a session ends. */
export function exerciseMinutesGeneration(): number {
  return generation;
}

/** Sends the refused choice again, as if the person had just made it. */
export function retryExerciseMinutes(): void {
  const again = refused;
  if (again === undefined) return;
  // A read since the refusal may have put the account's value back on the device.
  change(again);
  unsaved = again;
  storage?.saveUnsaved(again);
  refused = undefined;
  for (const listener of listeners) listener();
  for (const listener of choiceListeners) listener(again);
}

/** The choice still waiting to reach the account, kept across restarts. */
export function unsavedExerciseMinutes(): number | undefined {
  return unsaved;
}

/** The device's duration when it holds one of its own (stored or chosen), not the default. */
export function heldExerciseMinutes(): number | undefined {
  return held ? minutes : undefined;
}

/** Called with each duration the person chooses (`setExerciseMinutes`). */
export function onExerciseMinutesChosen(listener: (minutes: number) => void): () => void {
  choiceListeners.add(listener);
  return () => choiceListeners.delete(listener);
}

export function getExerciseMinutes(): number {
  return minutes;
}

const getRefused = () => refused;

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The chosen exercise duration, in minutes. */
export function useExerciseMinutes(): number {
  return useSyncExternalStore(subscribe, getExerciseMinutes, getExerciseMinutes);
}

/** The choice Core refused outright, while the person has neither retried nor chosen again. */
export function useRefusedExerciseMinutes(): number | undefined {
  return useSyncExternalStore(subscribe, getRefused, getRefused);
}
